import React, { useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Card } from './card';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { Textarea } from './textarea';

export interface FormField {
  name: string;
  label: string;
  type: 'text' | 'email' | 'password' | 'number' | 'date' | 'select' | 'textarea' | 'checkbox' | 'file';
  required?: boolean;
  options?: { value: string; label: string }[];
  placeholder?: string;
  validation?: {
    pattern?: RegExp;
    message?: string;
  };
  accept?: string; // for file types
}

export interface FormConfig {
  title: string;
  fields: FormField[];
  submitLabel?: string;
  cancelLabel?: string;
  tableName: string;
  onSuccess?: () => void;
  onCancel?: () => void;
  initialData?: Record<string, any>;
  mode?: 'create' | 'edit';
  recordId?: string;
}

export function Form({
  title,
  fields,
  submitLabel = 'Submit',
  cancelLabel = 'Cancel',
  tableName,
  onSuccess,
  onCancel,
  initialData = {},
  mode = 'create',
  recordId
}: FormConfig) {
  const { user } = useAuth();
  const [formData, setFormData] = useState<Record<string, any>>(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleInputChange = useCallback((name: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear validation error when field is modified
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [validationErrors]);

  const validateField = useCallback((field: FormField, value: any): string | null => {
    if (field.required && !value) {
      return `${field.label} is required`;
    }

    if (field.validation?.pattern && value) {
      const pattern = field.validation.pattern;
      if (!pattern.test(value)) {
        return field.validation.message || `${field.label} is invalid`;
      }
    }

    return null;
  }, []);

  const validateForm = useCallback((): boolean => {
    const errors: Record<string, string> = {};
    let isValid = true;

    fields.forEach(field => {
      const error = validateField(field, formData[field.name]);
      if (error) {
        errors[field.name] = error;
        isValid = false;
      }
    });

    setValidationErrors(errors);
    return isValid;
  }, [fields, formData, validateField]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      if (!user) {
        throw new Error('You must be logged in to perform this action');
      }

      const data = {
        ...formData,
        user_id: user.id
      };

      let result;

      if (mode === 'create') {
        result = await supabase
          .from(tableName)
          .insert(data)
          .select()
          .single();
      } else {
        if (!recordId) {
          throw new Error('Record ID is required for edit mode');
        }
        result = await supabase
          .from(tableName)
          .update(data)
          .eq('id', recordId)
          .select()
          .single();
      }

      if (result.error) throw result.error;

      // Reset form
      setFormData(initialData);
      setValidationErrors({});

      // Call success callback
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const renderField = (field: FormField) => {
    const commonProps = {
      id: field.name,
      value: formData[field.name] || '',
      onChange: (e: any) => handleInputChange(field.name, e.target.value),
      required: field.required,
      placeholder: field.placeholder,
      className: validationErrors[field.name] ? 'border-red-500' : ''
    };

    switch (field.type) {
      case 'select':
        return (
          <Select
            value={formData[field.name] || ''}
            onValueChange={(value) => handleInputChange(field.name, value)}
          >
            <SelectTrigger>
              <SelectValue placeholder={field.placeholder || `Select ${field.label}`} />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'textarea':
        return (
          <Textarea
            {...commonProps}
            rows={4}
          />
        );

      case 'checkbox':
        return (
          <input
            type="checkbox"
            checked={formData[field.name] || false}
            onChange={(e) => handleInputChange(field.name, e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
        );

      case 'file':
        return (
          <div>
            <input
              type="file"
              accept={field.accept || 'image/*'}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file || !user) return;
                setUploading(true);
                setUploadProgress(0);
                try {
                  const fileExt = file.name.split('.').pop();
                  const fileName = `${user.id}/${Date.now()}.${fileExt}`;
                  const filePath = `pet-photos/${fileName}`;
                  const { error: uploadError } = await supabase.storage
                    .from('pet-photos')
                    .upload(filePath, file, {
                      cacheControl: '3600',
                      upsert: false
                    });
                  if (uploadError) throw uploadError;
                  const { data: { publicUrl } } = supabase.storage
                    .from('pet-photos')
                    .getPublicUrl(filePath);
                  handleInputChange(field.name, publicUrl);
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'Failed to upload image');
                } finally {
                  setUploading(false);
                  setUploadProgress(0);
                }
              }}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            {uploading && <p className="text-xs text-blue-600 mt-1">Uploading...</p>}
            {formData[field.name] && (
              <img src={formData[field.name]} alt="Preview" className="mt-2 h-20 w-20 object-cover rounded" />
            )}
          </div>
        );

      default:
        return (
          <Input
            {...commonProps}
            type={field.type}
          />
        );
    }
  };

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <div className="p-6">
        <h3 className="text-lg font-semibold mb-4">{title}</h3>

        {error && (
          <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {fields.map(field => (
            <div key={field.name}>
              <Label htmlFor={field.name}>
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </Label>
              {renderField(field)}
              {validationErrors[field.name] && (
                <p className="mt-1 text-sm text-red-500">
                  {validationErrors[field.name]}
                </p>
              )}
            </div>
          ))}

          <div className="flex justify-end space-x-2 mt-6">
            {onCancel && (
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
              >
                {cancelLabel}
              </Button>
            )}
            <Button
              type="submit"
              disabled={loading}
            >
              {loading ? 'Saving...' : submitLabel}
            </Button>
          </div>
        </form>
      </div>
    </Card>
  );
} 